/**
 * Daily Deal — storefront helper.
 *
 * Responsibilities:
 *   1. Midnight section refresh (soft-reload via Section Rendering API) so
 *      the customer sees tomorrow's pick without a manual reload.
 *   2. CTA click beacon — POST /api/dd-events/click to Product Tools.
 *   3. Attribution stamp — store {dealDate, productId, variantId, ts} in
 *      sessionStorage on click.
 *   4. /cart/add interceptor — when a variant in the stamp gets added to
 *      cart (within 6h), POST /api/dd-events/cart-add with the cart_token.
 *      Global — applies on any page the visitor adds to cart from.
 *
 * No dependencies. Safe to load on any page. Idempotent.
 */

(function () {
  'use strict';

  const CONFIG = {
    apiUrl: 'https://product-tools.sanctionsnow.com',
    // apiUrl: 'http://localhost:3000', // Dev
    stampKey: 'dd.attrib.v1',
    visitorKey: 'dd.visitor.v1',
    stampTtlMs: 6 * 60 * 60 * 1000, // 6h window — must match ATC_WINDOW in dailyDealsAnalyticsService.js
  };

  const SECTION_SELECTOR = '.daily-deal-section';

  // ---------------------------------------------------------------------------
  // Visitor ID — random, stable across tabs, persists until the visitor clears
  // site data. Used only for de-duping "unique visitors" in analytics.
  // ---------------------------------------------------------------------------
  function getVisitorId() {
    try {
      let v = localStorage.getItem(CONFIG.visitorKey);
      if (!v) {
        v = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(CONFIG.visitorKey, v);
      }
      return v;
    } catch (_) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Midnight refresh — preserved from the original file.
  // ---------------------------------------------------------------------------
  function msToMidnight() {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    return midnight - now;
  }

  async function refreshSection(section) {
    const sectionId = section.dataset.sectionId;
    if (!sectionId) return;
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, {
        headers: { Accept: 'text/html' },
      });
      if (!res.ok) return;
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const fresh = doc.querySelector(SECTION_SELECTOR);
      if (!fresh) return;
      section.innerHTML = fresh.innerHTML;
      wireSection(section); // re-wire CTA listener after DOM swap
      scheduleMidnightRefresh(section);
    } catch (err) {
      console.warn('[daily-deal] section refresh failed', err);
    }
  }

  function scheduleMidnightRefresh(section) {
    if (section.__ddTimeout) clearTimeout(section.__ddTimeout);
    // +2s grace period so Product Tools' midnight cron has written the new metafield
    section.__ddTimeout = setTimeout(() => refreshSection(section), msToMidnight() + 2000);
  }

  // ---------------------------------------------------------------------------
  // Click tracking + attribution stamp.
  // ---------------------------------------------------------------------------
  function todayISO() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  function readSectionContext(section) {
    const article = section.querySelector('[data-deal-product-id]');
    if (!article) return null;
    const productId = article.dataset.dealProductId;
    const variantId = article.dataset.dealVariantId || null;
    if (!productId) return null;
    return { productId, variantId };
  }

  function writeStamp(productId, variantId) {
    const stamp = {
      dealDate: todayISO(),
      productId: String(productId),
      variantId: variantId != null ? String(variantId) : null,
      ts: Date.now(),
    };
    try { sessionStorage.setItem(CONFIG.stampKey, JSON.stringify(stamp)); } catch (_) { /* ignore */ }
    return stamp;
  }

  function readStamp() {
    try {
      const raw = sessionStorage.getItem(CONFIG.stampKey);
      if (!raw) return null;
      const stamp = JSON.parse(raw);
      if (!stamp || !stamp.productId || !stamp.dealDate) return null;
      if (Date.now() - Number(stamp.ts || 0) > CONFIG.stampTtlMs) {
        sessionStorage.removeItem(CONFIG.stampKey);
        return null;
      }
      return stamp;
    } catch (_) {
      return null;
    }
  }

  function postBeacon(path, payload) {
    const url = CONFIG.apiUrl + path;
    const body = JSON.stringify(payload);

    // Prefer sendBeacon when available — survives navigation (click → PDP).
    if (navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      } catch (_) { /* fall through to fetch */ }
    }

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(function () { /* swallow */ });
    } catch (_) { /* swallow */ }
  }

  function onClickCta(ctx) {
    writeStamp(ctx.productId, ctx.variantId);
    postBeacon('/api/dd-events/click', {
      dealDate: todayISO(),
      productId: ctx.productId,
      variantId: ctx.variantId,
      visitorId: getVisitorId(),
    });
  }

  function fireViewBeacon(ctx) {
    postBeacon('/api/dd-events/view', {
      dealDate: todayISO(),
      productId: ctx.productId,
      visitorId: getVisitorId(),
    });
  }

  // Fire a view only after the section is genuinely visible — ≥50% in the
  // viewport for ≥600ms. Stops below-the-fold visitors who never scroll from
  // counting as a view (which would tank the click-through rate denominator).
  // Falls back to "fire on render" on browsers that lack IntersectionObserver.
  function observeViewport(section, ctx) {
    var sessionKey = 'dd.view.' + todayISO() + '.' + ctx.productId;
    function alreadyFired() {
      try { return !!sessionStorage.getItem(sessionKey); } catch (_) { return false; }
    }
    function markFired() {
      try { sessionStorage.setItem(sessionKey, '1'); } catch (_) { /* ignore */ }
    }
    function fireOnce() {
      if (alreadyFired()) return;
      markFired();
      fireViewBeacon(ctx);
    }

    if (alreadyFired()) return;

    if (typeof IntersectionObserver === 'undefined') {
      // No-IO fallback: fire on idle so we don't block paint.
      var fallback = function () { fireOnce(); };
      if (window.requestIdleCallback) window.requestIdleCallback(fallback, { timeout: 2000 });
      else setTimeout(fallback, 1000);
      return;
    }

    var dwellTimer = null;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // Start the dwell timer — must stay ≥50% visible for 600ms.
          if (!dwellTimer) {
            dwellTimer = setTimeout(function () {
              fireOnce();
              observer.disconnect();
            }, 600);
          }
        } else if (dwellTimer) {
          // Scrolled away before the timer fired — reset, don't count as a view.
          clearTimeout(dwellTimer);
          dwellTimer = null;
        }
      });
    }, { threshold: [0.5] });

    observer.observe(section);
    section.__ddViewObserver = observer;
  }

  function wireSection(section) {
    if (section.__ddWired) return;
    const ctx = readSectionContext(section);
    if (!ctx) return;

    // View tracking — see observeViewport. Server-side dedupes per
    // (dealDate, visitorId) too, so this is belt + suspenders.
    observeViewport(section, ctx);

    // Fire on any CTA-like control in the card: the primary CTA, the image
    // link, and the title link. Each is an outbound <a> to the PDP.
    const triggers = section.querySelectorAll(
      '.daily-deal__cta, .daily-deal__image-link, .daily-deal__title a'
    );
    triggers.forEach(function (el) {
      el.addEventListener('click', function () { onClickCta(ctx); }, { passive: true });
    });
    section.__ddWired = true;
  }

  // ---------------------------------------------------------------------------
  // Global /cart/add interceptor. Loaded on every page by this script, since
  // the section only renders on the homepage but /cart/add fires from PDPs.
  // ---------------------------------------------------------------------------
  const CartAddTracker = {
    initialized: false,
    init: function () {
      if (this.initialized) return;
      this.initialized = true;

      const originalFetch = window.fetch;
      window.fetch = function (resource, init) {
        const url = (typeof resource === 'string')
          ? resource
          : (resource && resource.url ? resource.url : '');

        const result = originalFetch.apply(this, arguments);

        if (url && url.indexOf('/cart/add') !== -1) {
          Promise.resolve(result).then(function (response) {
            if (!response || !response.ok) return;
            let cloned;
            try { cloned = response.clone(); } catch (_) { return; }
            cloned.json().then(function (data) {
              CartAddTracker.maybeRecord(data).catch(function () { /* swallow */ });
            }, function () { /* non-JSON */ });
          }, function () { /* fetch failed */ });
        }

        return result;
      };
    },

    maybeRecord: async function (addResponseData) {
      const stamp = readStamp();
      if (!stamp) return;

      let items;
      if (Array.isArray(addResponseData.items) && addResponseData.items.length > 0) {
        items = addResponseData.items;
      } else if (addResponseData.variant_id) {
        items = [addResponseData];
      } else {
        return;
      }

      // Match by product_id — variant_id optional (new release from a PDP may
      // pick a size that wasn't in the stamp).
      const matched = items.filter(function (it) {
        return it && String(it.product_id) === String(stamp.productId);
      });
      if (matched.length === 0) return;

      let cartToken = null;
      try {
        const cartResp = await fetch('/cart.js');
        if (!cartResp.ok) return;
        const cart = await cartResp.json();
        // /cart.js returns "<token>?key=<hex>" but the orders webhook sends
        // the bare "<token>" — strip the suffix so attribution can match.
        cartToken = cart && cart.token && String(cart.token).split('?')[0];
      } catch (_) { return; }
      if (!cartToken) return;

      const visitorId = getVisitorId();
      for (let i = 0; i < matched.length; i++) {
        const item = matched[i];
        postBeacon('/api/dd-events/cart-add', {
          dealDate: stamp.dealDate,
          productId: String(item.product_id),
          variantId: String(item.variant_id),
          cartToken: cartToken,
          visitorId: visitorId,
          price: (item.price || 0) / 100,
          quantity: item.quantity || 1,
        });
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------
  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(function (section) {
      scheduleMidnightRefresh(section);
      wireSection(section);
    });
    CartAddTracker.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor — reinitialize when section is edited
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector
      ? e.target.querySelector(SECTION_SELECTOR)
      : null;
    if (section) {
      scheduleMidnightRefresh(section);
      wireSection(section);
    }
  });
})();
